import { call, delay, fork, put, race, select, take } from 'redux-saga/effects';
import * as FromClientFrontendActions from '../actions/from-client-frontend';
import * as FromClusterActions from '../actions/from-cluster';
import { httpRequestFailed, httpRequestStarted, httpRequestSucceeded } from '../actions/http-requests';
import { loadVisitorToken } from '../api/auth';
import { sendToCluster } from '../api/websocket';
import { requestVisitorAuthentication } from '../../../../src/actions/auth';
import { visitorTokenExpired, visitorAndClientTokenMatchUserId } from '../utils/jwt';
import { debugEvent } from '../../actions/debug';
import { getJwtBodyPart } from '../utils/jwt';

const VISITOR_AUTHENTICATED_TIMEOUT = 20000;

export function* handleLoadVisitorToken() {
  const { chatSession } = yield select();
  const { CHAT_API, username, clientToken } = chatSession;

  if (!CHAT_API || !username || !clientToken) {
    yield put(
      debugEvent({
        name: 'MISSING_CHAT_SESSION',
        payload: {
          CHAT_API,
          username,
          clientToken: getJwtBodyPart({ token: clientToken })
        }
      })
    );

    return null;
  }

  try {
    const visitorToken = yield call(loadVisitorToken, { CHAT_API, clientToken });
    yield put(FromClientFrontendActions.storeVisitorToken({ visitorToken }));
    return visitorToken;
  } catch (e) {
    console.error('Failed to load visitor token.');

    yield put(
      debugEvent({
        name: 'FAILED_TO_LOAD_VISITOR_TOKEN (catch)',
        payload: {
          err: e.message
        }
      })
    );

    return null;
  }
};

export function* waitForClusterAuthentication({ onAuthenticated = null, onFailedAuthentication = null }) {
  // waiting for event back from cluster

  const { authenticated, failure, timeout } = yield race({
    authenticated: take(FromClusterActions.VISITOR_AUTH_SUCCESS),
    failure: take(FromClusterActions.VISITOR_AUTH_FAILED),
    timeout: delay(VISITOR_AUTHENTICATED_TIMEOUT)
  });

  if (authenticated && typeof onAuthenticated === 'function') {
    return yield call(onAuthenticated);
  }

  if (failure || timeout) {
    if (typeof onFailedAuthentication === 'function') {
      const err = failure ? 'AUTH_FAILED' : 'AUTH_TIMEOUT';
      return yield call(onFailedAuthentication, err, failure);
    }
  }
}

export function* authenticateVisitor({
  onAuthenticated,
  onFailedToLoadVisitorToken,
  onFailedAuthentication = null
}) {
  const visitorToken = yield call(handleLoadVisitorToken);

  if (!visitorToken) {
    // failed to load visitor token
    return yield call(onFailedToLoadVisitorToken);
  }

  // wait for VISITOR_AUTH_SUCCESS confirm back from cluster
  yield fork(
    waitForClusterAuthentication, {
    onAuthenticated,
    onFailedAuthentication
  }
  );

  yield put(
    debugEvent({
      name: 'SEND_TO_CLUSTER_REQUEST_VISITOR_AUTH',
      payload: {
        visitorToken: getJwtBodyPart({ token: visitorToken })
      }
    })
  );

  return sendToCluster(
    requestVisitorAuthentication({ visitorToken })
  );
};

export function handleVisitorTokenRequest(saga, onRequestSucceeded) {
  return function*(action) {
    const { chatSession } = yield select();
    const { CHAT_API, username, visitorToken, lastChatVisitorToken } = chatSession;

    const actionToUse = {
      ...action,
      payload: {
        ...action.payload,
        _meta: undefined // Remove the _meta data before sending it to BE
      }
    };

    yield put(httpRequestStarted(action));

    try {
      const response = yield call(
        saga, {
        action: actionToUse,
        CHAT_API,
        visitorToken: visitorToken || lastChatVisitorToken
      });

      if (typeof onRequestSucceeded === 'function') {
        yield fork(onRequestSucceeded, response);
      }

      yield put(httpRequestSucceeded(action, response));
    } catch (e) {
      yield put(
        debugEvent({
          name: 'FAILED_CHAT_API_REQUEST',
          payload: {
            visitorToken: getJwtBodyPart({ token: visitorToken }),
            lastChatVisitorToken: getJwtBodyPart({ token: lastChatVisitorToken })
          }
        })
      );

      console.error('Failed chat api request...', e);
      yield put(httpRequestFailed(action, e));
    }
  };
};
